import cn from 'classnames';
import React, { useEffect, useState } from 'react';

import ColourSwatchView from './ColourSwatchView';
import { useColourSwatchModel } from './useColourSwatchModel';
import { useColourPickerContext } from '../ColourPickerContext';
import styles from './ColourSwatch.module.scss';

const IMAGE_SIZE_CARD_MOBILE = 16;
const IMAGE_SIZE_CARD_DESKTOP = 20;
const CARD_BREAKPOINT_PX = 720;

function useCardImageSize(): number {
  const [size, setSize] = useState(IMAGE_SIZE_CARD_DESKTOP);
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${CARD_BREAKPOINT_PX}px)`);
    setSize(mql.matches ? IMAGE_SIZE_CARD_DESKTOP : IMAGE_SIZE_CARD_MOBILE);
    const handler = () =>
      setSize(mql.matches ? IMAGE_SIZE_CARD_DESKTOP : IMAGE_SIZE_CARD_MOBILE);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return size;
}

export interface ColourSwatchCardProps {
  colourId: number;
}

/** PLP / compact card swatch — responsive image size + overflow tab order */
const ColourSwatchCard: React.FC<ColourSwatchCardProps> = ({ colourId }) => {
  const model = useColourSwatchModel(colourId);
  const { overflowCollapsedVisibleCount } = useColourPickerContext();
  const cardImageSize = useCardImageSize();

  if (!model) return null;

  const { colour, ...rest } = model;
  const index = colourId - 1;
  const isHiddenWhenCollapsed =
    overflowCollapsedVisibleCount != null &&
    index >= overflowCollapsedVisibleCount;

  return (
    <ColourSwatchView
      {...rest}
      colour={colour}
      wrapperClassName={styles.wrapper}
      buttonClassName={cn(styles.button, {
        [styles.buttonSelected]: colour.isSelected,
        [styles.buttonUnavailableForSize]: colour.isDisabled,
        [styles.buttonNoData]: model.isNoData,
      })}
      imageWidth={cardImageSize}
      imageHeight={cardImageSize}
      tabIndex={isHiddenWhenCollapsed ? -1 : undefined}
    />
  );
};

export default ColourSwatchCard;
